export class TextMeasurementUtil {
  private static canvas: HTMLCanvasElement | null = null;
  private static context: CanvasRenderingContext2D | null = null;
  private static measurementCache = new Map<string, number>();

  private static readonly CHECKBOX_RADIO_WIDTH = 32; 
  private static readonly HORIZONTAL_PADDING = 32; 
  private static readonly GAP_BETWEEN_ICON_TEXT = 8; 
  private static readonly TILE_BUFFER = 8; 
  private static readonly MIN_TILE_WIDTH = 70; 

  private static getContext(): CanvasRenderingContext2D {
    if (!this.canvas) {
      this.canvas = document.createElement('canvas');
      this.context = this.canvas.getContext('2d');
    }

    if (this.context) {
      const baseFontSize =
        getComputedStyle(document.documentElement)
          .getPropertyValue('--base-font-size')
          .trim() || '16px';
      this.context.font = `${baseFontSize} Onest, sans-serif`;
    }

    return this.context!;
  }

  static stripHtml(html: string): string {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  static measureText(text: string): number {
    const context = this.getContext();
    return context.measureText(text).width;
  }

  static getLongestWordWidth(text: string): number {
    const plainText = this.stripHtml(text);
    const words = plainText.split(/[\s\-\/\\,;:]+/).filter((w) => w.length > 0);

    if (words.length === 0) return 0;

    let maxWidth = 0;
    const context = this.getContext();

    for (const word of words) {
      const width = context.measureText(word).width;
      maxWidth = Math.max(maxWidth, width);
    }

    return maxWidth;
  }

  static calculateMinTileWidth(
    alternatives: { title: string }[],
    questionId?: string,
  ): number {
    if (questionId) {
      const cached = this.measurementCache.get(questionId);
      if (cached !== undefined) {
        return cached;
      }
    }

    let maxTextWidth = 0;
    const context = this.getContext();

    for (const alt of alternatives) {
      const plainText = this.stripHtml(alt.title || '');
      const textWidth = context.measureText(plainText).width;
      maxTextWidth = Math.max(maxTextWidth, textWidth);
    }

    const totalWidth = Math.ceil(
      this.CHECKBOX_RADIO_WIDTH +
        this.GAP_BETWEEN_ICON_TEXT +
        maxTextWidth +
        this.HORIZONTAL_PADDING +
        this.TILE_BUFFER,
    );

    let finalWidth = Math.max(totalWidth, this.MIN_TILE_WIDTH);

    finalWidth = this.roundToStableBreakpoint(finalWidth);

    if (questionId) {
      this.measurementCache.set(questionId, finalWidth);
    }

    return finalWidth;
  }

  private static roundToStableBreakpoint(width: number): number {
    const breakpointIncrement = 10;
    return Math.floor(width / breakpointIncrement) * breakpointIncrement;
  }

  static clearCache(): void {
    this.measurementCache.clear();
  }

  static fitsInSingleRow(
    alternatives: { title: string }[],
    containerWidth: number,
  ): boolean {
    if (!alternatives || alternatives.length === 0) return true;
    if (containerWidth <= 0) return false;

    const count = alternatives.length;
    const gapSize = 8;

    const availablePerTile = (containerWidth - (count - 1) * gapSize) / count;

    let maxTextWidth = 0;
    const context = this.getContext();
    for (const alt of alternatives) {
      const plainText = this.stripHtml(alt.title || '');
      const textWidth = context.measureText(plainText).width;
      maxTextWidth = Math.max(maxTextWidth, textWidth);
    }

    const requiredPerTile =
      this.CHECKBOX_RADIO_WIDTH +
      this.GAP_BETWEEN_ICON_TEXT +
      maxTextWidth +
      this.HORIZONTAL_PADDING +
      this.TILE_BUFFER;
    
    return availablePerTile >= requiredPerTile;
  }
}
